import { useParams } from '@ksyos/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPersonalSettingsData } from '@frontend/components/pages/personalSettings/types';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';

type PersonalSettingsContextType = {
    error: Error | null;
    isLoading: boolean;
    personalSettingsData: IPersonalSettingsData | undefined;
    refetch: () => Promise<void>;
};

const PersonalSettingsContext = createContext<PersonalSettingsContextType | undefined>(undefined);

export function PersonalSettingsProvider({ children }: { children: React.ReactNode }) {
    const [personalSettingsData, setPersonalSettingsData] = useState<IPersonalSettingsData>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { employmentId } = useParams() as { employmentId: string };
    const abortableFetch = useAbortableFetch();

    const loadPersonalSettingsData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const url = `/api/employments/${employmentId}/personalSettings`;
            const result = await abortableFetch.fetchJson(url, 'GET');
            setPersonalSettingsData(result.json.item);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch personal settings'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (employmentId) {
            loadPersonalSettingsData();
        }
    }, [employmentId]);

    const contextValue = useMemo(
        () => ({
            error,
            isLoading,
            personalSettingsData,
            refetch: loadPersonalSettingsData,
        }),
        [error, isLoading, personalSettingsData]
    );

    return <PersonalSettingsContext.Provider value={contextValue}>{children}</PersonalSettingsContext.Provider>;
}

export function usePersonalSettings() {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    const context = useContext(PersonalSettingsContext);
    if (context === undefined) {
        throw new Error('usePersonalSettings must be used within a PersonalSettingsProvider');
    }

    const isReadOnly =
        !context.personalSettingsData?.isActiveEmployment ||
        (isManager && !hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit));

    return {
        ...context,
        isReadOnly,
    };
}
